/**
 * The card pinned to the foot of the side rail.
 *
 * Four things the design groups into one block: the accuracy disclaimer, the confidentiality
 * marking, the language toggle and sign-out.
 *
 * ## Why the disclaimer is here rather than under the composer
 *
 * It used to sit beneath the message box, which is where most assistants put it and where it is
 * read exactly once. In the rail it is present on every screen of the application — the history and
 * the drafts as well as the chat — which matters because a draft copied out of this tool and pasted
 * into a customer email has left the screen that carried the warning.
 *
 * The confidentiality marking is not decoration either. This is an internal banking tool whose
 * screenshots end up in tickets and chat threads; "Confidential — Internal" in the corner of every
 * one of them is the cheapest control available.
 */

import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { LanguageToggle } from "./LanguageToggle";

export interface RailFooterProps {
  /**
   * Sign-out, or whatever the application puts beside the language toggle.
   *
   * A slot rather than a built-in control: signing out is a session concern and this component has
   * no business reaching into the session to do it.
   */
  action?: ReactNode;
}

export function RailFooter({ action }: RailFooterProps): JSX.Element {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col overflow-hidden rounded-xl border border-borderSubtle bg-surfaceRaised">
      <div className="flex flex-col gap-2 p-3">
        <p className="text-label-sm font-medium leading-normal text-secondary">
          {t("disclaimer.accuracy")}
        </p>
        <div className="flex flex-col gap-1 border-t border-surfaceSunken pt-2 text-label-sm font-semibold uppercase tracking-wide text-onPrimaryMuted">
          <span>{t("disclaimer.confidential")}</span>
          <span>{t("disclaimer.poweredBy")}</span>
        </div>
      </div>

      <div className="flex items-center justify-between gap-2 border-t border-borderSubtle bg-surface px-3 py-2">
        <LanguageToggle />
        {action}
      </div>
    </div>
  );
}
